<template>
  <div class="route-making-container" dir="rtl">
    <vue-element-loading :active="isLoading" spinner="bar-fade-scale" color="#082C69" />

    <!-- ==================== HEADER ==================== -->
    <div class="rm-header-row mb-6">
      <div class="rm-header-titles">
        <div class="d-flex align-center gap-2 mb-2 flex-wrap">
          <span class="rm-badge-section">تفاصيل المجموعة</span>
          <div class="rm-breadcrumb">
            <router-link to="/trip-groups" class="rm-bc-link">مجموعات الرحلات</router-link>
            <v-icon x-small color="#94A3B8" class="mx-1">mdi-chevron-left</v-icon>
            <span class="rm-bc-item rm-bc-active">{{ group.group_code || '—' }}</span>
          </div>
        </div>

        <div class="d-flex align-center gap-3 flex-wrap">
          <h1 class="rm-main-title">تفاصيل المجموعة: {{ group.group_code }}</h1>
          <span
            class="rm-status-badge"
            :class="{
              'status-published': group.status === 'published',
              'status-draft': group.status === 'draft',
              'status-inactive': group.status === 'inactive'
            }"
          >
            <span class="status-dot"></span>
            {{ group.status_label }}
          </span>
        </div>
        <p class="rm-subtitle">{{ group.name || 'مجموعة رحلات تشغيلية' }}</p>
      </div>

      <div class="rm-header-actions d-flex align-center gap-2 flex-wrap">
        <v-btn
          depressed
          class="rm-btn-secondary"
          @click="goBack"
        >
          <v-icon right size="18" class="ms-1">mdi-arrow-right</v-icon>
          العودة للقائمة
        </v-btn>

        <v-btn
          depressed
          class="rm-btn-secondary"
          @click="exportTripsCsv"
          :disabled="plannedTrips.length === 0"
        >
          <v-icon right size="18" class="ms-1">mdi-download</v-icon>
          تصدير الرحلات CSV
        </v-btn>

        <v-btn
          depressed
          class="rm-btn-primary"
          :to="`/trip-groups/${groupId}/edit`"
        >
          <v-icon right size="18" class="ms-1">mdi-pencil</v-icon>
          تعديل المجموعة
        </v-btn>
      </div>
    </div>

    <!-- ==================== TOP KPI SUMMARY CARDS ==================== -->
    <div class="rm-cards-grid mb-6">
      <!-- 1. Total KM -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">إجمالي الكيلومترات</span>
          <div class="rm-stat-val-wrapper">
            <span class="rm-stat-value">{{ summary.total_distance_km }} <small class="text-sm font-weight-bold">كم</small></span>
          </div>
          <span class="rm-stat-caption">إجمالي مسافات الرحلات المشمولة</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-blue">
          <v-icon size="26" color="#082C69">mdi-road</v-icon>
        </div>
      </div>

      <!-- 2. Trips Count -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">عدد الرحلات</span>
          <div class="rm-stat-val-wrapper">
            <span class="rm-stat-value text-navy">{{ summary.trips_count }}</span>
          </div>
          <span class="rm-stat-caption">رحلات مجدولة داخل المجموعة</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-green">
          <v-icon size="26" color="#10B981">mdi-bus-clock</v-icon>
        </div>
      </div>

      <!-- 3. Creation Date -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">تاريخ الإنشاء</span>
          <div class="rm-stat-val-wrapper">
            <span class="rm-stat-value rm-val-date">{{ group.created_at || '—' }}</span>
          </div>
          <span class="rm-stat-caption">تاريخ تسجيل المجموعة بالنظام</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-amber">
          <v-icon size="26" color="#F59E0B">mdi-calendar-check</v-icon>
        </div>
      </div>

      <!-- 4. Status -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">الحالة الحالية</span>
          <div class="rm-stat-val-wrapper">
            <span class="rm-stat-value rm-val-status">{{ group.status_label }}</span>
          </div>
          <span class="rm-stat-caption">{{ group.published_at ? `تاريخ النشر: ${group.published_at}` : 'قيد الإعداد' }}</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-blue">
          <v-icon size="26" color="#082C69">mdi-shield-check-outline</v-icon>
        </div>
      </div>
    </div>

    <!-- ==================== MAIN 2-COLUMN LAYOUT ==================== -->
    <div class="rm-details-grid">
      <!-- ==================== LEFT COLUMN: MAIN CONTENT (65%) ==================== -->
      <div class="rm-main-col">
        <!-- 1. Operation Assignment History Table -->
        <div class="rm-card mb-6">
          <div class="rm-card-header d-flex align-center justify-space-between mb-4 flex-wrap gap-2">
            <div class="d-flex align-center">
              <div class="rm-icon-header-box me-3">
                <v-icon size="20" color="#082C69">mdi-account-tie</v-icon>
              </div>
              <div>
                <h2 class="rm-card-title">سجل تكليفات التشغيل</h2>
                <p class="rm-card-subtitle">متابعة مسؤولي التشغيل المكلفين بإدارة هذه المجموعة</p>
              </div>
            </div>

            <v-btn
              v-if="assignmentHistory.length > 0"
              text
              small
              color="#082C69"
              class="font-weight-bold"
              @click="exportAssignmentsCsv"
            >
              <v-icon left small>mdi-download</v-icon>
              تصدير السجل
            </v-btn>
          </div>

          <!-- Assignment History Table Content -->
          <div v-if="assignmentHistory.length > 0" class="table-responsive">
            <table class="rm-custom-table">
              <thead>
                <tr>
                  <th style="width: 40px;" class="text-center">#</th>
                  <th>مسؤول التشغيل</th>
                  <th>تاريخ بداية التكليف</th>
                  <th>تاريخ نهاية التكليف</th>
                  <th class="text-center">الحالة</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in assignmentHistory" :key="item.id">
                  <td class="text-center text-muted">{{ item.index }}</td>
                  <td>
                    <div class="d-flex align-center">
                      <v-avatar size="24" color="#082C69" class="white--text me-2 caption font-weight-bold">
                        {{ item.user_name ? item.user_name.charAt(0) : '?' }}
                      </v-avatar>
                      <span class="font-weight-bold text-navy">{{ item.user_name }}</span>
                    </div>
                  </td>
                  <td class="caption">{{ item.assigned_at || '—' }}</td>
                  <td class="caption">{{ item.unassigned_at || '—' }}</td>
                  <td class="text-center">
                    <span
                      class="rm-assignment-pill"
                      :class="item.is_current ? 'assignment-current' : 'assignment-prev'"
                    >
                      {{ item.status_label }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="rm-empty-assignment-box pa-6 rounded-lg text-center">
            <v-icon size="36" color="#94A3B8" class="mb-2">mdi-account-question-outline</v-icon>
            <h4 class="font-weight-bold text-navy mb-1">لم يتم تعيين مسؤول تشغيل لهذه المجموعة بعد</h4>
            <p class="caption text-muted mb-0">سيتم تفعيل سجل تكليفات التشغيل فور إطلاق دور التشغيل (Operations Role) في النظام.</p>
          </div>
        </div>

        <!-- 2. Group Trips Table -->
        <div class="rm-card">
          <div class="rm-card-header d-flex align-center justify-space-between mb-4 flex-wrap gap-2">
            <div class="d-flex align-center">
              <div class="rm-icon-header-box me-3">
                <v-icon size="20" color="#082C69">mdi-bus-multiple</v-icon>
              </div>
              <div>
                <h2 class="rm-card-title">الرحلات الأساسية المدرجة في المجموعة</h2>
                <p class="rm-card-subtitle">قائمة الرحلات الأساسية (Parent Trips) وتوقيتاتها ومسافاتها</p>
              </div>
            </div>

            <div class="d-flex align-center gap-2">
              <span class="rm-badge-count">{{ plannedTrips.length }} رحلات أساسية</span>
              <v-btn
                v-if="plannedTrips.length > 0"
                text
                small
                color="#082C69"
                class="font-weight-bold"
                @click="exportTripsCsv"
              >
                <v-icon left small>mdi-download</v-icon>
                تصدير الرحلات
              </v-btn>
            </div>
          </div>

          <!-- Trips Table Content -->
          <div v-if="plannedTrips.length > 0" class="table-responsive">
            <table class="rm-custom-table">
              <thead>
                <tr>
                  <th style="width: 40px;" class="text-center">#</th>
                  <th>كود الرحلة</th>
                  <th>المسار</th>
                  <th>وقت الانطلاق</th>
                  <th>وقت الوصول</th>
                  <th>أيام التشغيل</th>
                  <th class="text-center">التشغيلات القادمة</th>
                  <th class="text-center">المسافة</th>
                  <th class="text-center">الحالة</th>
                  <th class="text-center">الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="pt in plannedTrips" :key="pt.id">
                  <td class="text-center text-muted font-weight-medium">{{ pt.index }}</td>
                  <td>
                    <span class="rm-code-pill font-weight-bold">{{ pt.trip_code || pt.trip_reference || `T-${String(pt.id).padStart(4, '0')}` }}</span>
                  </td>
                  <td>
                    <div class="d-flex flex-column">
                      <span class="font-weight-bold text-navy">{{ pt.route_name }}</span>
                      <span class="caption text-muted">{{ pt.route_code }}</span>
                    </div>
                  </td>
                  <td class="caption">{{ pt.first_stop_time ? pt.first_stop_time.slice(0, 5) : '—' }}</td>
                  <td class="caption">{{ pt.last_stop_time ? pt.last_stop_time.slice(0, 5) : '—' }}</td>
                  <td class="caption">
                    <span class="rm-days-badge">{{ formatOperatingDays(pt.operating_days) }}</span>
                  </td>
                  <td class="text-center">
                    <span class="rm-badge-count">{{ pt.upcoming_occurrences_count || 0 }}</span>
                  </td>
                  <td class="text-center caption font-weight-bold">{{ pt.distance_km }} كم</td>
                  <td class="text-center">
                    <span :class="['rm-trip-status-pill', pt.status_id === 1 ? 'status-scheduled' : 'status-cancelled']">{{ pt.status_label }}</span>
                  </td>
                  <td class="text-center">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          icon
                          small
                          v-bind="attrs"
                          v-on="on"
                          :to="`/trips/view-trip/trip=${pt.id}`"
                          class="rm-action-btn"
                        >
                          <v-icon small color="#082C69">mdi-eye-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>عرض تفاصيل الرحلة الأساسية</span>
                    </v-tooltip>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="text-center py-8 text-muted caption">
            لا توجد رحلات أساسية مدرجة في هذه المجموعة حتى الآن
          </div>
        </div>
      </div>

      <!-- ==================== RIGHT COLUMN: SIDEBAR INFO & MAP (35%) ==================== -->
      <div class="rm-side-col">
        <!-- 1. Group Information Card -->
        <div class="rm-card mb-6">
          <div class="rm-card-header d-flex align-center mb-4">
            <div class="rm-icon-header-box me-3">
              <v-icon size="20" color="#082C69">mdi-information-outline</v-icon>
            </div>
            <h2 class="rm-card-title">معلومات المجموعة</h2>
          </div>

          <div class="rm-info-list">
            <div class="rm-info-row d-flex justify-space-between py-2 border-bottom">
              <span class="caption text-muted">كود المجموعة</span>
              <span class="caption font-weight-bold text-navy">{{ group.group_code }}</span>
            </div>

            <div class="rm-info-row d-flex justify-space-between py-2 border-bottom">
              <span class="caption text-muted">اسم المجموعة</span>
              <span class="caption font-weight-bold text-navy">{{ group.name }}</span>
            </div>

            <div class="rm-info-row d-flex justify-space-between py-2 border-bottom">
              <span class="caption text-muted">الحالة</span>
              <span class="caption font-weight-bold">{{ group.status_label }}</span>
            </div>

            <div class="rm-info-row d-flex justify-space-between py-2 border-bottom">
              <span class="caption text-muted">مسؤول التشغيل</span>
              <span class="caption font-weight-bold text-navy">
                {{ currentOperationUser ? currentOperationUser.name : 'غير معيّن' }}
              </span>
            </div>

            <div class="rm-info-row d-flex justify-space-between py-2 border-bottom">
              <span class="caption text-muted">تاريخ النشر</span>
              <span class="caption text-muted">{{ group.published_at || 'لم تنشر بعد' }}</span>
            </div>

            <div class="rm-info-row d-flex justify-space-between py-2">
              <span class="caption text-muted">تاريخ الإنشاء</span>
              <span class="caption text-muted">{{ group.created_at || '—' }}</span>
            </div>
          </div>
        </div>

        <!-- 2. Routes Summary Card -->
        <div class="rm-card mb-6">
          <div class="rm-card-header d-flex align-center mb-3">
            <div class="rm-icon-header-box me-3">
              <v-icon size="20" color="#082C69">mdi-routes</v-icon>
            </div>
            <div>
              <h2 class="rm-card-title">ملخص المسارات</h2>
              <span class="caption text-muted">{{ summary.unique_routes_count }} مسارات مختلفة</span>
            </div>
          </div>

          <!-- Covered Cities / Regions -->
          <div v-if="summary.covered_cities && summary.covered_cities.length > 0" class="mb-3">
            <span class="caption text-muted d-block mb-1 font-weight-bold">المناطق والمدن المغطاة:</span>
            <div class="d-flex flex-wrap gap-1">
              <span
                v-for="city in summary.covered_cities"
                :key="city"
                class="rm-city-tag"
              >
                {{ city }}
              </span>
            </div>
          </div>

          <!-- Routes List -->
          <div class="rm-routes-mini-list">
            <div
              v-for="r in routes"
              :key="r.id"
              class="rm-route-mini-item d-flex align-center justify-space-between p-2 mb-2 rounded"
            >
              <div>
                <span class="caption font-weight-bold text-navy">{{ r.name }}</span>
                <span class="caption text-muted d-block">{{ r.code }}</span>
              </div>
              <span class="caption font-weight-bold text-navy">{{ r.distance_km }} كم</span>
            </div>
          </div>
        </div>

        <!-- 3. Group Routes Map -->
        <div class="rm-card">
          <div class="rm-card-header d-flex align-center justify-space-between mb-3">
            <div class="d-flex align-center">
              <div class="rm-icon-header-box me-3">
                <v-icon size="20" color="#082C69">mdi-map-outline</v-icon>
              </div>
              <h2 class="rm-card-title">خريطة مسارات المجموعة</h2>
            </div>
          </div>

          <!-- Map View -->
          <div class="rm-group-map-wrapper rounded-lg overflow-hidden">
            <GoogleMapLoader
              v-if="mapPolylines.length > 0 || mapMarkers.length > 0"
              ref="mapLoader"
              :apiKey="apiKey"
              :mapConfig="mapConfig"
              :center="mapCenter"
              :zoom="12"
              :markers="mapMarkers"
              :polylines="mapPolylines"
              class="rm-map-element"
            />
            <div v-else class="text-center py-12 text-muted caption bg-slate-50">
              <v-icon size="32" color="#CBD5E1" class="d-block mb-2">mdi-map-marker-off-outline</v-icon>
              لا تتوفر إحداثيات جغرافية لعرض الخريطة
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { Keys } from "@/config.js";
import GoogleMapLoader from "@/components/GoogleMapLoader.vue";
import { exportToCsv } from "@/utils/csvExport";

export default {
  name: "RouteMakingTripGroupDetails",
  components: {
    GoogleMapLoader,
  },
  data() {
    return {
      isLoading: false,
      groupId: null,
      group: {},
      summary: {
        trips_count: 0,
        total_distance_km: 0,
        unique_routes_count: 0,
        covered_cities: [],
      },
      currentOperationUser: null,
      plannedTrips: [],
      assignmentHistory: [],
      routes: [],
      // Google Map
      apiKey: Keys.GOOGLE_MAPS_API_KEY,
      mapConfig: {
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
      },
      mapCenter: { lat: 30.0444, lng: 31.2357 }, // Cairo default
      mapPolylines: [],
      mapMarkers: [],
    };
  },
  created() {
    this.groupId = this.$route.params.id;
    if (this.groupId) {
      this.fetchGroupDetails();
    }
  },
  methods: {
    fetchGroupDetails() {
      this.isLoading = true;
      axios
        .get(`/trip-groups/${this.groupId}`)
        .then((res) => {
          if (res.data && res.data.success) {
            const data = res.data.data;
            this.group = data.group || {};
            this.summary = data.summary || {};
            this.currentOperationUser = data.current_operation_user || null;
            this.plannedTrips = data.trips || data.planned_trips || [];
            this.assignmentHistory = data.assignment_history || [];
            this.routes = data.routes || [];

            this.buildMapData();
          }
        })
        .catch((err) => {
          console.error("Error loading trip group details:", err);
          this.notifyUser("error", "خطأ", "تعذر تحميل تفاصيل مجموعة الرحلات");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    buildMapData() {
      const polylines = [];
      const markers = [];
      const colors = ["#082C69", "#0284C7", "#10B981", "#F59E0B", "#8B5CF6"];

      this.routes.forEach((route, rIdx) => {
        const color = colors[rIdx % colors.length];

        // Polyline
        if (Array.isArray(route.polyline) && route.polyline.length > 0) {
          polylines.push({
            data: route.polyline,
            strokeColor: color,
            strokeWeight: 4,
            strokeOpacity: 0.85,
          });

          if (!this.mapCenter || (this.mapCenter.lat === 30.0444 && this.mapCenter.lng === 31.2357)) {
            this.mapCenter = route.polyline[0];
          }
        }

        // Stops markers
        if (Array.isArray(route.stops)) {
          route.stops.forEach((s, sIdx) => {
            if (s.lat && s.lng) {
              const isStart = sIdx === 0;
              const isEnd = sIdx === route.stops.length - 1;
              markers.push({
                position: { lat: Number(s.lat), lng: Number(s.lng) },
                title: `${route.name} - ${s.name}`,
                label: String(sIdx + 1),
                icon: isStart ? "https://maps.google.com/mapfiles/ms/icons/green-dot.png" : (isEnd ? "https://maps.google.com/mapfiles/ms/icons/red-dot.png" : "https://maps.google.com/mapfiles/ms/icons/blue-dot.png"),
              });
            }
          });
        }
      });

      this.mapPolylines = polylines;
      this.mapMarkers = markers;
    },
    formatOperatingDays(days) {
      if (!days) return "—";
      let arr = days;
      if (typeof days === "string") {
        try {
          arr = JSON.parse(days);
        } catch (e) {
          return days;
        }
      }
      if (!Array.isArray(arr) || arr.length === 0) return "—";

      const sunThu = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس"];
      if (arr.length === 5 && sunThu.every((d) => arr.includes(d))) {
        return "الأحد – الخميس";
      }
      if (arr.length === 7) {
        return "يومياً";
      }
      return arr.join("، ");
    },
    getTripStatusClass(trip) {
      const code = trip.status_code || trip.status_label;
      if (code === "cancelled" || code === "ملغاة") return "status-cancelled";
      if (code === "completed" || code === "مكتملة") return "status-completed";
      if (code === "in_progress" || code === "جاري التشغيل") return "status-in-progress";
      return "status-scheduled";
    },
    exportTripsCsv() {
      if (this.plannedTrips.length === 0) return;

      const headers = [
        "كود المجموعة",
        "كود الرحلة الأساسية",
        "المسار",
        "كود المسار",
        "وقت الانطلاق",
        "وقت الوصول المتوقع",
        "أيام التشغيل",
        "عدد التشغيلات القادمة",
        "المسافة القياسية (كم)",
        "الحالة",
      ];

      const rows = this.plannedTrips.map((pt) => [
        this.group.group_code,
        pt.trip_code || pt.trip_reference || `T-${String(pt.id).padStart(4, '0')}`,
        pt.route_name,
        pt.route_code,
        pt.first_stop_time ? pt.first_stop_time.slice(0, 5) : "—",
        pt.last_stop_time ? pt.last_stop_time.slice(0, 5) : "—",
        this.formatOperatingDays(pt.operating_days),
        pt.upcoming_occurrences_count || 0,
        pt.distance_km,
        pt.status_label,
      ]);

      const metadata = [
        { label: "المجموعة", value: `${this.group.group_code} - ${this.group.name}` },
        { label: "الحالة", value: this.group.status_label },
        { label: "تاريخ الاستخراج", value: new Date().toISOString().slice(0, 10) },
        { label: "عدد الرحلات الأساسية", value: rows.length },
      ];

      exportToCsv({
        filename: `trip-group-${this.group.group_code}-parent-trips.csv`,
        headers,
        rows,
        metadata,
      });
    },
    exportAssignmentsCsv() {
      if (this.assignmentHistory.length === 0) return;

      const headers = [
        "#",
        "مسؤول التشغيل",
        "تاريخ بداية التكليف",
        "تاريخ نهاية التكليف",
        "الحالة",
      ];

      const rows = this.assignmentHistory.map((a) => [
        a.index,
        a.user_name,
        a.assigned_at || "—",
        a.unassigned_at || "—",
        a.status_label,
      ]);

      const metadata = [
        { label: "سجل تكليفات التشغيل للمجموعة", value: `${this.group.group_code} - ${this.group.name}` },
        { label: "تاريخ الاستخراج", value: new Date().toISOString().slice(0, 10) },
      ];

      exportToCsv({
        filename: `trip-group-${this.group.group_code}-assignments.csv`,
        headers,
        rows,
        metadata,
      });
    },
    goBack() {
      this.$router.push("/trip-groups");
    },
    notifyUser(type, title, text) {
      if (this.$swal) {
        this.$swal({
          icon: type,
          title: title,
          text: text,
          timer: 3500,
          showConfirmButton: false,
        });
      }
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800&display=swap");

.route-making-container {
  font-family: "Cairo", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  direction: rtl;
  text-align: right;
  color: #1E293B;
  padding: 8px 4px 40px 4px;
}

/* Header */
.rm-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.rm-badge-section {
  display: inline-block;
  background-color: rgba(8, 44, 105, 0.08);
  color: #082C69;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 6px;
}

.rm-breadcrumb {
  font-size: 12px;
  color: #64748B;
}

.rm-bc-link {
  color: #082C69;
  text-decoration: none;
  font-weight: 600;
}
.rm-bc-link:hover {
  text-decoration: underline;
}

.rm-main-title {
  font-size: 24px;
  font-weight: 800;
  color: #082C69;
  margin: 0;
  line-height: 1.3;
}

.rm-subtitle {
  font-size: 13px;
  color: #64748B;
  margin: 4px 0 0 0;
}

/* Stat Cards */
.rm-cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.rm-stat-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.rm-stat-content {
  display: flex;
  flex-direction: column;
}

.rm-stat-label {
  font-size: 12px;
  font-weight: 600;
  color: #64748B;
  margin-bottom: 2px;
}

.rm-stat-value {
  font-size: 26px;
  font-weight: 800;
  color: #082C69;
  line-height: 1.2;
}

.rm-val-date {
  font-size: 18px;
}

.rm-val-status {
  font-size: 20px;
}

.rm-stat-caption {
  font-size: 11px;
  color: #94A3B8;
  margin-top: 2px;
}

.rm-stat-icon-box {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rm-icon-blue { background: #EEF2F6; }
.rm-icon-green { background: #ECFDF5; }
.rm-icon-amber { background: #FFFBEB; }

/* Grid */
.rm-details-grid {
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 24px;
  align-items: start;
}

@media (max-width: 1024px) {
  .rm-details-grid {
    grid-template-columns: 1fr;
  }
}

/* Cards */
.rm-card {
  background: #FFFFFF;
  border: 1px solid #E2E8F0;
  border-radius: 14px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.rm-card-title {
  font-size: 16px;
  font-weight: 700;
  color: #082C69;
  margin: 0;
}

.rm-card-subtitle {
  font-size: 12px;
  color: #64748B;
  margin: 2px 0 0 0;
}

.rm-icon-header-box {
  width: 36px;
  height: 36px;
  background: #EEF2F6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Table */
.table-responsive {
  overflow-x: auto;
}

.rm-custom-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.rm-custom-table th {
  background-color: #F8FAFC;
  color: #475569;
  font-weight: 700;
  padding: 10px 12px;
  text-align: right;
  border-bottom: 2px solid #E2E8F0;
  white-space: nowrap;
}

.rm-custom-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #F1F5F9;
  vertical-align: middle;
}

.rm-code-pill {
  display: inline-block;
  background: #EEF2F6;
  color: #082C69;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 6px;
}

.rm-trip-status-pill {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
}

.status-scheduled {
  background-color: #ECFDF5 !important;
  color: #065F46 !important;
  border: 1px solid #A7F3D0 !important;
}

.status-in-progress {
  background-color: #EFF6FF !important;
  color: #1E40AF !important;
  border: 1px solid #BFDBFE !important;
}

.status-completed {
  background-color: #F1F5F9 !important;
  color: #475569 !important;
  border: 1px solid #CBD5E1 !important;
}

.status-cancelled {
  background-color: #FEF2F2 !important;
  color: #991B1B !important;
  border: 1px solid #FECACA !important;
}

/* Assignment Badges */
.rm-assignment-pill {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 12px;
}

.assignment-current {
  background-color: #ECFDF5;
  color: #047857;
}

.assignment-prev {
  background-color: #F1F5F9;
  color: #64748B;
}

.rm-empty-assignment-box {
  background-color: #F8FAFC;
  border: 1px dashed #CBD5E1;
}

/* Status Badges */
.rm-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 700;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.status-published {
  background-color: #ECFDF5;
  color: #047857;
}
.status-published .status-dot { background-color: #10B981; }

.status-draft {
  background-color: #FFFBEB;
  color: #B45309;
}
.status-draft .status-dot { background-color: #F59E0B; }

.status-inactive {
  background-color: #F1F5F9;
  color: #64748B;
}
.status-inactive .status-dot { background-color: #94A3B8; }

.rm-badge-count {
  background-color: #082C69;
  color: #FFFFFF;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
}

/* Map */
.rm-group-map-wrapper {
  height: 280px;
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
}

.rm-map-element {
  width: 100%;
  height: 100%;
}

.rm-city-tag {
  background-color: #EEF2F6;
  color: #082C69;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 6px;
}

.rm-route-mini-item {
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
}

/* Buttons */
.rm-btn-primary {
  background-color: #082C69 !important;
  color: #FFFFFF !important;
  font-family: "Cairo", sans-serif !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  border-radius: 10px !important;
  text-transform: none !important;
  padding: 0 16px !important;
  height: 40px !important;
}

.rm-btn-secondary {
  background-color: #FFFFFF !important;
  color: #082C69 !important;
  border: 1px solid #CBD5E1 !important;
  font-family: "Cairo", sans-serif !important;
  font-weight: 700 !important;
  font-size: 13px !important;
  border-radius: 10px !important;
  text-transform: none !important;
  padding: 0 14px !important;
  height: 40px !important;
}

.rm-action-btn {
  background: #F8FAFC !important;
  border: 1px solid #E2E8F0 !important;
  border-radius: 8px !important;
}

.text-navy { color: #082C69 !important; }
.border-bottom { border-bottom: 1px solid #F1F5F9; }
</style>
